'use client'

import React, { useEffect, useRef } from 'react'
import { cn, generateInitials } from '../../../../../lib/utils'
import { Expired } from '../../components/Expired'
import { type ButtonProps } from '../../../Button'
import ChatInputBar, {
  type ChatInputBarProps,
} from '../../components/ChatInputBar'
import ChatTopbar, { type ChatTopbarProps } from '../../components/ChatTopBar'
import { ChatList, type ChatListProps } from '../../components/ChatList'
import { Avatar, AvatarFallback, AvatarImage } from '../../../Avatar'
import { AlertWrapper, type AlertProps } from '../../../Alert'

import Icon, { iconTypes } from '../../../Icon'
import Link from 'next/link'
import StagedMessage, { type StagedMessageProps } from '../StagedMessage'
import SuggestedResponse, {
  type SuggestedResponseProps,
} from '../SuggestedResponse'

export interface ChatWindowProps {
  title: string
  isLoading: boolean
  mode?: 'inline' | 'modal'
  view: 'open' | 'collapsed' | 'hidden'
  height?: number
  chatList: ChatListProps
  chatInputBar: ChatInputBarProps
  chatTopBar: ChatTopbarProps
  topAlert?: AlertProps
  stagedMessage?: StagedMessageProps
  suggestedResponse?: SuggestedResponseProps
  expired?: {
    message: string
    action: ButtonProps
  }
  className?: string
  size?: 'sm' | 'default'
}

export const generateAvatar = (props: {
  name?: string
  img?: string | undefined
  seed?: string
  isLoading?: boolean
  collapsed?: boolean
  icon?: iconTypes
  handleClick?: () => void | Promise<void>
  href?: string
}) => {
  const { name, img, isLoading, collapsed, icon, handleClick, href, seed } =
    props

  const hasImg = Boolean(img)
  const hasSeed = !hasImg && Boolean(seed)
  const hasIcon = !hasImg && !hasSeed && Boolean(icon)

  const renderContent = (withPulse?: boolean) => (
    <>
      {hasImg && (
        <AvatarImage
          src={img}
          seed={seed}
          alt={name}
          className={cn('w-12 h-12', { 'animate-pulse': withPulse })}
        />
      )}
      {!hasImg && hasSeed && (
        <AvatarImage
          seed={seed}
          alt={name}
          className={cn('w-12 h-12', { 'animate-pulse': withPulse })}
        />
      )}
      {!hasImg && !hasSeed && hasIcon && <Icon size={20} name={icon!} />}
      {!hasImg && !hasSeed && !hasIcon && (
        <AvatarFallback>{generateInitials(name || '')}</AvatarFallback>
      )}
    </>
  )

  if (isLoading) {
    return (
      <>
        {href ? (
          <Link
            href={href}
            className={cn(
              'w-12 h-12 flex justify-center items-center animate-pulse',
              {
                'w-8 h-8': collapsed,
              },
            )}
          >
            <Avatar
              className={cn(
                'w-12 h-12 flex justify-center items-center animate-pulse',
                {
                  'w-8 h-8': collapsed,
                },
              )}
            >
              {renderContent(true)}
            </Avatar>
          </Link>
        ) : (
          <button
            className={cn(
              'w-12 h-12 flex justify-center items-center animate-pulse',
              {
                'w-8 h-8': collapsed,
              },
            )}
            onClick={handleClick}
          >
            <Avatar
              className={cn('w-12 h-12 flex justify-center items-center', {
                'w-8 h-8': collapsed,
              })}
            >
              {renderContent(true)}
            </Avatar>
          </button>
        )}
      </>
    )
  }

  return (
    <Avatar
      className={cn('w-12 h-12 flex justify-center items-center', {
        'w-8 h-8': collapsed,
      })}
    >
      {renderContent()}
    </Avatar>
  )
}

export function ChatWindow({
  title,
  view,
  mode = 'modal',
  height = 600,
  chatList,
  chatInputBar,
  stagedMessage,
  suggestedResponse,
  expired,
  chatTopBar,
  topAlert,
  className,
  size = 'default',
}: ChatWindowProps) {
  const chatWindowRef = React.useRef<HTMLDivElement>(null)
  const messageContainerRef = React.useRef<HTMLDivElement>(null)
  const lastComponentIdRef = useRef<string | undefined>(undefined)
  const isCollapsed = view === 'collapsed'
  const isHidden = view === 'hidden'

  // Scroll to bottom when last message changes
  useEffect(() => {
    if (!chatList.components.length || isCollapsed) return

    const lastComponent = chatList.components[chatList.components.length - 1]
    if (!lastComponent) return
    const lastComponentId =
      lastComponent.type === 'message'
        ? lastComponent.props.props.id
        : lastComponent.props.props.id

    if (lastComponentId === lastComponentIdRef.current) return
    lastComponentIdRef.current = lastComponentId

    if (messageContainerRef.current) {
      messageContainerRef.current.scrollTop =
        messageContainerRef.current.scrollHeight
    }
  }, [chatList.components, isCollapsed])

  return (
    <div
      className={cn(
        'bg-background flex flex-col border-2 rounded-2xl shadow-lg',
        'transition-[height,transform,opacity] duration-200 ease-out',
        {
          'absolute bottom-4 right-4 w-[400px]': mode === 'modal',
          'w-full': mode === 'inline',
          'h-[600px]': !isCollapsed && !isHidden,
          'h-[60px]': isCollapsed,
          'translate-y-full': isHidden,
          'opacity-0': isHidden,
          'hidden md:flex': mode === 'modal',
          'transform-origin-bottom-right scale-[0.7]': size === 'sm',
        },
        className,
      )}
      style={{
        transformOrigin: size === 'sm' ? 'bottom right' : 'bottom',
      }}
      title={`${title}-${view}`}
      ref={chatWindowRef}
    >
      <div
        className={cn(
          'w-full flex flex-col transition-transform duration-200 ease-out',
          {
            'h-full': !isCollapsed,
            'h-[60px]': isCollapsed,
          },
        )}
      >
        {!expired && (
          <div className="flex flex-col w-full h-full">
            <div
              className={cn('px-2', {
                'py-2': !isCollapsed,
                'py-[8px]': isCollapsed,
              })}
            >
              <ChatTopbar
                {...chatTopBar}
                title={title}
                view={view}
                className="border-2 border-border/40"
              />
              {topAlert && (
                <div className="mt-2">
                  <AlertWrapper {...topAlert} className={cn('shadow-md')} />
                </div>
              )}
            </div>
            {!isCollapsed && (
              <>
                <div
                  ref={messageContainerRef}
                  className="flex-1 min-h-0 overflow-y-auto px-2"
                >
                  <ChatList
                    {...chatList}
                    title={title}
                    scrollContainer={messageContainerRef.current}
                  />
                </div>

                {suggestedResponse && (
                  <div className="flex-none px-2 pt-2">
                    <SuggestedResponse {...suggestedResponse} compact />
                  </div>
                )}

                <div className="flex-none">
                  <ChatInputBar title={title} {...chatInputBar} />
                </div>

                {stagedMessage && <StagedMessage {...stagedMessage} />}
              </>
            )}
          </div>
        )}

        {expired && (
          <div className="flex flex-col w-full h-full gap-2 p-2 pt-0 grow items-center justify-center">
            <Expired {...expired} />
          </div>
        )}
      </div>
    </div>
  )
}
